/**
 * Settings Types
 *
 * Type definitions for user settings stored in Firestore
 * Structure: users/{userId}/settings/{category}
 *
 * ## Architecture
 *
 * This module provides two types of exports for each settings category:
 *
 * 1. **Data Interfaces** (e.g., `ProfileSettingsData`, `InvestableAssetsSettingsData`):
 *    - Plain TypeScript interfaces for data serialization
 *    - Used in Firestore operations (reading/writing documents)
 *    - Used in React components for form state
 *    - Used in service layer for data transfer
 *
 * 2. **Classes** (e.g., `ProfileSettings`, `InvestableAssetsSettings`):
 *    - Classes with validation and computed methods
 *    - Used for business logic and calculations
 *    - Used when you need computed values (e.g., `getTotalInvestableAssets()`)
 *    - Used for input validation (constructor throws on invalid data)
 *
 * ## When to Use Each
 *
 * **Use Data Interfaces when:**
 * - Storing/retrieving from Firestore
 * - Managing form state in React components
 * - Passing data between service functions
 * - No validation or computation needed
 *
 * **Use Classes when:**
 * - You need validation (e.g., ensure non-negative numbers)
 * - You need computed values (e.g., total assets, total expenses)
 * - You want to ensure data invariants
 * - Converting external data to validated application data
 *
 * ## Example Usage
 *
 * ```typescript
 * // Form state uses data interface
 * const [formData, setFormData] = useState<InvestableAssetsSettingsData>({
 *   rrspBalance: 0,
 *   tfsaBalance: 0,
 *   nonRegisteredInvestments: 0,
 *   cashSavings: 0
 * });
 *
 * // Validate and compute with class
 * try {
 *   const assets = new InvestableAssetsSettings(formData);
 *   const total = assets.getTotalInvestableAssets(); // Method computes total
 * } catch (error) {
 *   // handle error
 * }
 *
 * // Firestore operations use data interface
 * await updateSettings(userId, 'investableAssets', formData);
 * ```
 */

import BigNumber from "bignumber.js";
import type { NonNegativeNumber } from "./numeric";
import { toNonNegativeNumber } from "./numeric";

/**
 * Canadian province/territory codes (ISO 3166-2:CA)
 */
export const PROVINCE_CODES = [
  "AB",
  "BC",
  "MB",
  "NB",
  "NL",
  "NS",
  "NT",
  "NU",
  "ON",
  "PE",
  "QC",
  "SK",
  "YT",
] as const;

export type ProvinceCode = (typeof PROVINCE_CODES)[number];

/**
 * Marital status options for Canadian tax/benefit calculations
 */
export const MARITAL_STATUS_OPTIONS = [
  "single",
  "married",
  "common-law",
  "separated",
  "widowed",
] as const;

export type MaritalStatus = (typeof MARITAL_STATUS_OPTIONS)[number];

/**
 * Withdrawal order strategies for retirement decumulation
 */
export const WITHDRAWAL_ORDER_OPTIONS = [
  "standard",
  "tfsa-last",
  "rrsp-first",
  "pro-rata",
  "tax-optimal",
] as const;

export type WithdrawalOrder = (typeof WITHDRAWAL_ORDER_OPTIONS)[number];

/**
 * Planned one-time expense (e.g., renovation, wedding, car purchase)
 */
export interface PlannedExpense {
  description: string;
  amount: number;
  year: number;
}

/**
 * Settings category types
 */
export type SettingsCategory =
  | "profile"
  | "investableAssets"
  | "realEstateAssets"
  | "liabilities"
  | "income"
  | "expenses"
  | "assumptions";

/**
 * Profile settings data interface (plain data for JSON serialization)
 * Note: firstName, lastName, email, avatarUrl are stored in the main user document,
 * not in settings. Only user-editable profile fields go here.
 *
 * yearOfBirth can be null for new users until they complete onboarding
 */
export interface ProfileSettingsData {
  yearOfBirth: number | null; // Birth year (e.g., 1990) or null for new users
  isRetired: boolean | null; // Whether the user is retired, null means not yet selected (required)
  // Expanded profile fields (optional for backward compatibility)
  province?: ProvinceCode;
  maritalStatus?: MaritalStatus;
  spouseYearOfBirth?: number | null;
  retirementAge?: number;
  lifeExpectancy?: number;
  cppStartAge?: number;
  oasStartAge?: number;
  yearsInCanadaAfterAge18?: number;
}

/**
 * Investable assets settings data interface (liquid portfolio)
 * Covers cash, registered accounts, non-registered investments,
 * and employer-sponsored plans.
 */
export interface InvestableAssetsSettingsData {
  cashSavings: number;
  emergencyFund: number;
  gicTermDeposits: number;
  rrspBalance: number;
  spousalRrspBalance: number;
  tfsaBalance: number;
  fhsaBalance: number;
  respBalance: number;
  liraBalance: number;
  rrifBalance: number;
  dpspBalance: number;
  nonRegisteredInvestments: number;
  dbPensionCommutedValue: number;
  dcPensionBalance: number;
  groupRrspBalance: number;
  stockOptionsRsusValue: number;
  // Index signature for dynamic field access
  [key: string]: number;
}

/**
 * Investable assets settings class with validation and computed methods
 */
export class InvestableAssetsSettings {
  cashSavings: NonNegativeNumber;
  emergencyFund: NonNegativeNumber;
  gicTermDeposits: NonNegativeNumber;
  rrspBalance: NonNegativeNumber;
  spousalRrspBalance: NonNegativeNumber;
  tfsaBalance: NonNegativeNumber;
  fhsaBalance: NonNegativeNumber;
  respBalance: NonNegativeNumber;
  liraBalance: NonNegativeNumber;
  rrifBalance: NonNegativeNumber;
  dpspBalance: NonNegativeNumber;
  nonRegisteredInvestments: NonNegativeNumber;
  dbPensionCommutedValue: NonNegativeNumber;
  dcPensionBalance: NonNegativeNumber;
  groupRrspBalance: NonNegativeNumber;
  stockOptionsRsusValue: NonNegativeNumber;

  constructor(data: InvestableAssetsSettingsData) {
    this.cashSavings = toNonNegativeNumber(data.cashSavings ?? 0);
    this.emergencyFund = toNonNegativeNumber(data.emergencyFund ?? 0);
    this.gicTermDeposits = toNonNegativeNumber(data.gicTermDeposits ?? 0);
    this.rrspBalance = toNonNegativeNumber(data.rrspBalance ?? 0);
    this.spousalRrspBalance = toNonNegativeNumber(data.spousalRrspBalance ?? 0);
    this.tfsaBalance = toNonNegativeNumber(data.tfsaBalance ?? 0);
    this.fhsaBalance = toNonNegativeNumber(data.fhsaBalance ?? 0);
    this.respBalance = toNonNegativeNumber(data.respBalance ?? 0);
    this.liraBalance = toNonNegativeNumber(data.liraBalance ?? 0);
    this.rrifBalance = toNonNegativeNumber(data.rrifBalance ?? 0);
    this.dpspBalance = toNonNegativeNumber(data.dpspBalance ?? 0);
    this.nonRegisteredInvestments = toNonNegativeNumber(
      data.nonRegisteredInvestments ?? 0
    );
    this.dbPensionCommutedValue = toNonNegativeNumber(
      data.dbPensionCommutedValue ?? 0
    );
    this.dcPensionBalance = toNonNegativeNumber(data.dcPensionBalance ?? 0);
    this.groupRrspBalance = toNonNegativeNumber(data.groupRrspBalance ?? 0);
    this.stockOptionsRsusValue = toNonNegativeNumber(
      data.stockOptionsRsusValue ?? 0
    );
  }

  /**
   * Calculate total investable assets
   */
  getTotalInvestableAssets(): NonNegativeNumber {
    const total = new BigNumber(this.cashSavings)
      .plus(this.emergencyFund)
      .plus(this.gicTermDeposits)
      .plus(this.rrspBalance)
      .plus(this.spousalRrspBalance)
      .plus(this.tfsaBalance)
      .plus(this.fhsaBalance)
      .plus(this.respBalance)
      .plus(this.liraBalance)
      .plus(this.rrifBalance)
      .plus(this.dpspBalance)
      .plus(this.nonRegisteredInvestments)
      .plus(this.dbPensionCommutedValue)
      .plus(this.dcPensionBalance)
      .plus(this.groupRrspBalance)
      .plus(this.stockOptionsRsusValue)
      .toNumber();
    return toNonNegativeNumber(total);
  }

  /**
   * Convert to plain object for JSON serialization
   */
  toJSON(): InvestableAssetsSettingsData {
    return {
      cashSavings: this.cashSavings,
      emergencyFund: this.emergencyFund,
      gicTermDeposits: this.gicTermDeposits,
      rrspBalance: this.rrspBalance,
      spousalRrspBalance: this.spousalRrspBalance,
      tfsaBalance: this.tfsaBalance,
      fhsaBalance: this.fhsaBalance,
      respBalance: this.respBalance,
      liraBalance: this.liraBalance,
      rrifBalance: this.rrifBalance,
      dpspBalance: this.dpspBalance,
      nonRegisteredInvestments: this.nonRegisteredInvestments,
      dbPensionCommutedValue: this.dbPensionCommutedValue,
      dcPensionBalance: this.dcPensionBalance,
      groupRrspBalance: this.groupRrspBalance,
      stockOptionsRsusValue: this.stockOptionsRsusValue,
    };
  }

  /**
   * Create InvestableAssetsSettings from plain object
   */
  static fromJSON(
    data: InvestableAssetsSettingsData
  ): InvestableAssetsSettings {
    return new InvestableAssetsSettings(data);
  }
}

/**
 * Real estate and other physical assets settings data interface
 */
export interface RealEstateAssetsSettingsData {
  primaryResidence: number;
  vacationProperty: number;
  investmentProperties: number;
  landOtherRealEstate: number;
  businessValue: number;
  shareholderLoansReceivable: number;
  insuranceCashValue: number;
  otherAssets: number;
  depreciatingAssets: number;
  // Index signature for dynamic field access
  [key: string]: number;
}

/**
 * Real estate and other physical assets settings class with validation
 */
export class RealEstateAssetsSettings {
  primaryResidence: NonNegativeNumber;
  vacationProperty: NonNegativeNumber;
  investmentProperties: NonNegativeNumber;
  landOtherRealEstate: NonNegativeNumber;
  businessValue: NonNegativeNumber;
  shareholderLoansReceivable: NonNegativeNumber;
  insuranceCashValue: NonNegativeNumber;
  otherAssets: NonNegativeNumber;
  depreciatingAssets: NonNegativeNumber;

  constructor(data: RealEstateAssetsSettingsData) {
    this.primaryResidence = toNonNegativeNumber(data.primaryResidence ?? 0);
    this.vacationProperty = toNonNegativeNumber(data.vacationProperty ?? 0);
    this.investmentProperties = toNonNegativeNumber(
      data.investmentProperties ?? 0
    );
    this.landOtherRealEstate = toNonNegativeNumber(
      data.landOtherRealEstate ?? 0
    );
    this.businessValue = toNonNegativeNumber(data.businessValue ?? 0);
    this.shareholderLoansReceivable = toNonNegativeNumber(
      data.shareholderLoansReceivable ?? 0
    );
    this.insuranceCashValue = toNonNegativeNumber(data.insuranceCashValue ?? 0);
    this.otherAssets = toNonNegativeNumber(data.otherAssets ?? 0);
    this.depreciatingAssets = toNonNegativeNumber(data.depreciatingAssets ?? 0);
  }

  /**
   * Calculate total real estate and other physical assets
   */
  getTotalRealEstateAssets(): NonNegativeNumber {
    const total = new BigNumber(this.primaryResidence)
      .plus(this.vacationProperty)
      .plus(this.investmentProperties)
      .plus(this.landOtherRealEstate)
      .plus(this.businessValue)
      .plus(this.shareholderLoansReceivable)
      .plus(this.insuranceCashValue)
      .plus(this.otherAssets)
      .plus(this.depreciatingAssets)
      .toNumber();
    return toNonNegativeNumber(total);
  }

  /**
   * Convert to plain object for JSON serialization
   */
  toJSON(): RealEstateAssetsSettingsData {
    return {
      primaryResidence: this.primaryResidence,
      vacationProperty: this.vacationProperty,
      investmentProperties: this.investmentProperties,
      landOtherRealEstate: this.landOtherRealEstate,
      businessValue: this.businessValue,
      shareholderLoansReceivable: this.shareholderLoansReceivable,
      insuranceCashValue: this.insuranceCashValue,
      otherAssets: this.otherAssets,
      depreciatingAssets: this.depreciatingAssets,
    };
  }

  /**
   * Create RealEstateAssetsSettings from plain object
   */
  static fromJSON(
    data: RealEstateAssetsSettingsData
  ): RealEstateAssetsSettings {
    return new RealEstateAssetsSettings(data);
  }
}

/**
 * Liabilities settings data interface (plain data for JSON serialization)
 */
export interface LiabilitiesSettingsData {
  // Liability balances
  mortgage: number;
  creditCardBalance: number;
  carLoan: number;
  lineOfCredit: number;
  studentLoans: number;
  otherLiabilities: number;

  // Interest rates (stored as decimals, e.g., 0.05 for 5%)
  mortgageInterestRate: number;
  creditCardInterestRate: number;
  carLoanInterestRate: number;
  lineOfCreditInterestRate: number;
  studentLoanInterestRate: number;
  otherLiabilitiesInterestRate: number;

  // Expanded liability fields (optional for backward compatibility)
  investmentPropertyMortgage?: number;
  investmentPropertyMortgageRate?: number;
  heloc?: number;
  helocInterestRate?: number;
  businessLoans?: number;
  businessLoansInterestRate?: number;
  taxesPayable?: number;
  mortgageAmortizationRemaining?: number;

  // Index signature for dynamic field access
  [key: string]: number | undefined;
}

/**
 * Liabilities settings class with validation and computed methods
 */
export class LiabilitiesSettings {
  // Balances
  mortgage: NonNegativeNumber;
  creditCardBalance: NonNegativeNumber;
  carLoan: NonNegativeNumber;
  lineOfCredit: NonNegativeNumber;
  studentLoans: NonNegativeNumber;
  otherLiabilities: NonNegativeNumber;

  // Interest rates
  mortgageInterestRate: NonNegativeNumber;
  creditCardInterestRate: NonNegativeNumber;
  carLoanInterestRate: NonNegativeNumber;
  lineOfCreditInterestRate: NonNegativeNumber;
  studentLoanInterestRate: NonNegativeNumber;
  otherLiabilitiesInterestRate: NonNegativeNumber;

  // Expanded fields
  investmentPropertyMortgage: NonNegativeNumber;
  investmentPropertyMortgageRate: NonNegativeNumber;
  heloc: NonNegativeNumber;
  helocInterestRate: NonNegativeNumber;
  businessLoans: NonNegativeNumber;
  businessLoansInterestRate: NonNegativeNumber;
  taxesPayable: NonNegativeNumber;
  mortgageAmortizationRemaining: NonNegativeNumber;

  constructor(data: LiabilitiesSettingsData) {
    // Balances
    this.mortgage = toNonNegativeNumber(data.mortgage);
    this.creditCardBalance = toNonNegativeNumber(data.creditCardBalance);
    this.carLoan = toNonNegativeNumber(data.carLoan);
    this.lineOfCredit = toNonNegativeNumber(data.lineOfCredit);
    this.studentLoans = toNonNegativeNumber(data.studentLoans);
    this.otherLiabilities = toNonNegativeNumber(data.otherLiabilities);

    // Interest rates (default to 0 if not provided for backward compatibility)
    this.mortgageInterestRate = toNonNegativeNumber(
      data.mortgageInterestRate ?? 0
    );
    this.creditCardInterestRate = toNonNegativeNumber(
      data.creditCardInterestRate ?? 0
    );
    this.carLoanInterestRate = toNonNegativeNumber(
      data.carLoanInterestRate ?? 0
    );
    this.lineOfCreditInterestRate = toNonNegativeNumber(
      data.lineOfCreditInterestRate ?? 0
    );
    this.studentLoanInterestRate = toNonNegativeNumber(
      data.studentLoanInterestRate ?? 0
    );
    this.otherLiabilitiesInterestRate = toNonNegativeNumber(
      data.otherLiabilitiesInterestRate ?? 0
    );

    // Expanded fields (default to 0 for backward compatibility)
    this.investmentPropertyMortgage = toNonNegativeNumber(
      data.investmentPropertyMortgage ?? 0
    );
    this.investmentPropertyMortgageRate = toNonNegativeNumber(
      data.investmentPropertyMortgageRate ?? 0
    );
    this.heloc = toNonNegativeNumber(data.heloc ?? 0);
    this.helocInterestRate = toNonNegativeNumber(data.helocInterestRate ?? 0);
    this.businessLoans = toNonNegativeNumber(data.businessLoans ?? 0);
    this.businessLoansInterestRate = toNonNegativeNumber(
      data.businessLoansInterestRate ?? 0
    );
    this.taxesPayable = toNonNegativeNumber(data.taxesPayable ?? 0);
    this.mortgageAmortizationRemaining = toNonNegativeNumber(
      data.mortgageAmortizationRemaining ?? 0
    );
  }

  /**
   * Calculate total liabilities
   */
  getTotalLiabilities(): NonNegativeNumber {
    const totalLiabilities = new BigNumber(this.mortgage)
      .plus(this.creditCardBalance)
      .plus(this.carLoan)
      .plus(this.lineOfCredit)
      .plus(this.studentLoans)
      .plus(this.otherLiabilities)
      .plus(this.investmentPropertyMortgage)
      .plus(this.heloc)
      .plus(this.businessLoans)
      .plus(this.taxesPayable)
      .toNumber();
    return toNonNegativeNumber(totalLiabilities);
  }

  /**
   * Convert to plain object for JSON serialization
   */
  toJSON(): LiabilitiesSettingsData {
    return {
      mortgage: this.mortgage,
      creditCardBalance: this.creditCardBalance,
      carLoan: this.carLoan,
      lineOfCredit: this.lineOfCredit,
      studentLoans: this.studentLoans,
      otherLiabilities: this.otherLiabilities,
      mortgageInterestRate: this.mortgageInterestRate,
      creditCardInterestRate: this.creditCardInterestRate,
      carLoanInterestRate: this.carLoanInterestRate,
      lineOfCreditInterestRate: this.lineOfCreditInterestRate,
      studentLoanInterestRate: this.studentLoanInterestRate,
      otherLiabilitiesInterestRate: this.otherLiabilitiesInterestRate,
      investmentPropertyMortgage: this.investmentPropertyMortgage,
      investmentPropertyMortgageRate: this.investmentPropertyMortgageRate,
      heloc: this.heloc,
      helocInterestRate: this.helocInterestRate,
      businessLoans: this.businessLoans,
      businessLoansInterestRate: this.businessLoansInterestRate,
      taxesPayable: this.taxesPayable,
      mortgageAmortizationRemaining: this.mortgageAmortizationRemaining,
    };
  }

  /**
   * Create LiabilitiesSettings from plain object
   */
  static fromJSON(data: LiabilitiesSettingsData): LiabilitiesSettings {
    return new LiabilitiesSettings(data);
  }
}

/**
 * Income settings data interface (plain data for JSON serialization)
 * All income values are monthly amounts
 */
export interface IncomeSettingsData {
  // Employment income (monthly)
  employmentIncome: number;

  // Retirement income (monthly, shown based on age)
  pensionIncome: number; // Workplace pension or RRIF withdrawals
  cppIncome: number; // Canada Pension Plan - shown if age >= 60
  oasIncome: number; // Old Age Security - shown if age >= 65

  // Investment and rental income (monthly)
  investmentIncome: number;
  rentalIncome: number;

  // Other income (monthly)
  otherIncome: number;

  // Expanded income fields (optional for backward compatibility)
  bonusVariableIncome?: number; // Monthly avg bonus/commissions
  salaryGrowthRate?: number; // Annual salary increase rate (decimal, e.g. 0.03)
  businessIncome?: number; // Net monthly business income

  // Index signature for dynamic field access
  [key: string]: number | undefined;
}

/**
 * Income settings class with validation and computed methods
 * All income values are monthly amounts
 */
export class IncomeSettings {
  employmentIncome: NonNegativeNumber;
  pensionIncome: NonNegativeNumber;
  cppIncome: NonNegativeNumber;
  oasIncome: NonNegativeNumber;
  investmentIncome: NonNegativeNumber;
  rentalIncome: NonNegativeNumber;
  otherIncome: NonNegativeNumber;

  constructor(data: IncomeSettingsData) {
    this.employmentIncome = toNonNegativeNumber(data.employmentIncome || 0);
    this.pensionIncome = toNonNegativeNumber(data.pensionIncome || 0);
    this.cppIncome = toNonNegativeNumber(data.cppIncome || 0);
    this.oasIncome = toNonNegativeNumber(data.oasIncome || 0);
    this.investmentIncome = toNonNegativeNumber(data.investmentIncome || 0);
    this.rentalIncome = toNonNegativeNumber(data.rentalIncome || 0);
    this.otherIncome = toNonNegativeNumber(data.otherIncome || 0);
  }

  /**
   * Calculate total monthly income
   *
   * All income fields are stored as monthly values.
   * Use this method for all comparisons and calculations.
   */
  getMonthlyIncome(): NonNegativeNumber {
    const totalIncome = new BigNumber(this.employmentIncome)
      .plus(this.pensionIncome)
      .plus(this.cppIncome)
      .plus(this.oasIncome)
      .plus(this.investmentIncome)
      .plus(this.rentalIncome)
      .plus(this.otherIncome)
      .toNumber();
    return toNonNegativeNumber(totalIncome);
  }

  /**
   * Convert to plain object for JSON serialization
   */
  toJSON(): IncomeSettingsData {
    return {
      employmentIncome: this.employmentIncome,
      pensionIncome: this.pensionIncome,
      cppIncome: this.cppIncome,
      oasIncome: this.oasIncome,
      investmentIncome: this.investmentIncome,
      rentalIncome: this.rentalIncome,
      otherIncome: this.otherIncome,
    };
  }

  /**
   * Create IncomeSettings from plain object
   */
  static fromJSON(data: IncomeSettingsData): IncomeSettings {
    return new IncomeSettings(data);
  }
}

/**
 * Expenses settings data interface (plain data for JSON serialization)
 */
export interface ExpensesSettingsData {
  // Regular expenses
  housing: number;
  utilities: number;
  food: number;
  transportation: number;
  insurance: number;
  healthcare: number;
  other: number;
  personalDiscretionary?: number; // Optional for backward compatibility

  // Debt payments (monthly)
  mortgagePayment: number;
  creditCardPayment: number;
  carLoanPayment: number;
  lineOfCreditPayment: number;
  studentLoanPayment: number;
  otherLiabilitiesPayment: number;

  // One-time future expenses
  plannedExpenses?: PlannedExpense[];

  // Index signature for dynamic field access
  [key: string]: number | PlannedExpense[] | undefined;
}

/**
 * Expenses settings class with validation and computed methods
 */
export class ExpensesSettings {
  // Regular expenses
  housing: NonNegativeNumber;
  utilities: NonNegativeNumber;
  food: NonNegativeNumber;
  transportation: NonNegativeNumber;
  insurance: NonNegativeNumber;
  healthcare: NonNegativeNumber;
  other: NonNegativeNumber;
  personalDiscretionary: NonNegativeNumber;

  // Debt payments
  mortgagePayment: NonNegativeNumber;
  creditCardPayment: NonNegativeNumber;
  carLoanPayment: NonNegativeNumber;
  lineOfCreditPayment: NonNegativeNumber;
  studentLoanPayment: NonNegativeNumber;
  otherLiabilitiesPayment: NonNegativeNumber;

  constructor(data: ExpensesSettingsData) {
    // Regular expenses
    this.housing = toNonNegativeNumber(data.housing);
    this.utilities = toNonNegativeNumber(data.utilities);
    this.food = toNonNegativeNumber(data.food);
    this.transportation = toNonNegativeNumber(data.transportation);
    this.insurance = toNonNegativeNumber(data.insurance);
    this.healthcare = toNonNegativeNumber(data.healthcare);
    this.other = toNonNegativeNumber(data.other);
    this.personalDiscretionary = toNonNegativeNumber(
      data.personalDiscretionary ?? 0
    );

    // Debt payments (default to 0 if not provided for backward compatibility)
    this.mortgagePayment = toNonNegativeNumber(data.mortgagePayment ?? 0);
    this.creditCardPayment = toNonNegativeNumber(data.creditCardPayment ?? 0);
    this.carLoanPayment = toNonNegativeNumber(data.carLoanPayment ?? 0);
    this.lineOfCreditPayment = toNonNegativeNumber(
      data.lineOfCreditPayment ?? 0
    );
    this.studentLoanPayment = toNonNegativeNumber(data.studentLoanPayment ?? 0);
    this.otherLiabilitiesPayment = toNonNegativeNumber(
      data.otherLiabilitiesPayment ?? 0
    );
  }

  /**
   * Calculate monthly expenses (including all debt payments)
   */
  getMonthlyExpenses(): NonNegativeNumber {
    const totalExpenses = new BigNumber(this.housing)
      .plus(this.utilities)
      .plus(this.food)
      .plus(this.transportation)
      .plus(this.insurance)
      .plus(this.healthcare)
      .plus(this.other)
      .plus(this.personalDiscretionary)
      .plus(this.mortgagePayment)
      .plus(this.creditCardPayment)
      .plus(this.carLoanPayment)
      .plus(this.lineOfCreditPayment)
      .plus(this.studentLoanPayment)
      .plus(this.otherLiabilitiesPayment)
      .toNumber();
    return toNonNegativeNumber(totalExpenses);
  }

  /**
   * Calculate total debt payments only
   */
  getTotalDebtPayments(): NonNegativeNumber {
    const totalDebtPayments = new BigNumber(this.mortgagePayment)
      .plus(this.creditCardPayment)
      .plus(this.carLoanPayment)
      .plus(this.lineOfCreditPayment)
      .plus(this.studentLoanPayment)
      .plus(this.otherLiabilitiesPayment)
      .toNumber();
    return toNonNegativeNumber(totalDebtPayments);
  }

  /**
   * Calculate annual expenses (monthly * 12)
   */
  getAnnualExpenses(): NonNegativeNumber {
    const annualExpenses = new BigNumber(this.getMonthlyExpenses())
      .times(12)
      .toNumber();
    return toNonNegativeNumber(annualExpenses);
  }

  /**
   * Convert to plain object for JSON serialization
   */
  toJSON(): ExpensesSettingsData {
    return {
      housing: this.housing,
      utilities: this.utilities,
      food: this.food,
      transportation: this.transportation,
      insurance: this.insurance,
      healthcare: this.healthcare,
      other: this.other,
      personalDiscretionary: this.personalDiscretionary,
      mortgagePayment: this.mortgagePayment,
      creditCardPayment: this.creditCardPayment,
      carLoanPayment: this.carLoanPayment,
      lineOfCreditPayment: this.lineOfCreditPayment,
      studentLoanPayment: this.studentLoanPayment,
      otherLiabilitiesPayment: this.otherLiabilitiesPayment,
    };
  }

  /**
   * Create ExpensesSettings from plain object
   */
  static fromJSON(data: ExpensesSettingsData): ExpensesSettings {
    return new ExpensesSettings(data);
  }
}

/**
 * Assumptions settings data interface (plain data for JSON serialization)
 * Note: currentAge is NOT stored here - it's calculated from profile.yearOfBirth
 */
export interface AssumptionsSettingsData {
  retirementAge: number;
  lifeExpectancy: number;
  expectedReturnRate: number;
  inflationRate: number;
  expensesAtRetirement: number; // Decimal change in expenses at retirement (e.g., -0.20 for 20% reduction, 0.10 for 10% increase)
  realEstateAppreciationRate: number; // Annual appreciation rate for real estate (e.g., 0.03 for 3%)
  otherAssetGrowthRate: number; // Annual growth rate for other appreciable assets (e.g., 0.02 for 2%)
  depreciationRate: number; // Annual depreciation rate for depreciable assets (e.g., 0.15 for 15%)

  // Portfolio allocation (optional for backward compatibility)
  cashEquivalentsPct?: number;
  fixedIncomePct?: number;
  equitiesPct?: number;
  cashEquivalentsReturn?: number;
  fixedIncomeReturn?: number;
  equitiesReturn?: number;
  healthcareInflationBuffer?: number;
  withdrawalOrder?: WithdrawalOrder;
  estimatedRetirementTaxRate?: number;
  desiredLegacyAmount?: number;

  // Index signature for dynamic field access
  [key: string]: number | string | undefined;
}

/**
 * Assumptions settings class with validation
 * Note: currentAge is NOT stored here - it's calculated from profile.yearOfBirth
 */
export class AssumptionsSettings {
  retirementAge: NonNegativeNumber;
  lifeExpectancy: NonNegativeNumber;
  expectedReturnRate: NonNegativeNumber;
  inflationRate: NonNegativeNumber;
  expensesAtRetirement: number; // Can be negative (reduction) or positive (increase), stored as decimal (e.g., -0.20 = 20% reduction)
  realEstateAppreciationRate: NonNegativeNumber;
  otherAssetGrowthRate: NonNegativeNumber;
  depreciationRate: NonNegativeNumber;

  // Portfolio allocation
  cashEquivalentsPct: NonNegativeNumber;
  fixedIncomePct: NonNegativeNumber;
  equitiesPct: NonNegativeNumber;
  cashEquivalentsReturn: NonNegativeNumber;
  fixedIncomeReturn: NonNegativeNumber;
  equitiesReturn: NonNegativeNumber;
  healthcareInflationBuffer: NonNegativeNumber;
  withdrawalOrder: WithdrawalOrder;
  estimatedRetirementTaxRate: NonNegativeNumber;
  desiredLegacyAmount: NonNegativeNumber;

  constructor(data: AssumptionsSettingsData) {
    this.retirementAge = toNonNegativeNumber(data.retirementAge);
    this.lifeExpectancy = toNonNegativeNumber(data.lifeExpectancy);
    this.expectedReturnRate = toNonNegativeNumber(data.expectedReturnRate);
    this.inflationRate = toNonNegativeNumber(data.inflationRate);
    // Use expensesAtRetirement if available, otherwise default to 0 (no change)
    this.expensesAtRetirement = data.expensesAtRetirement ?? 0;
    this.realEstateAppreciationRate = toNonNegativeNumber(
      data.realEstateAppreciationRate ?? 0.03
    );
    this.otherAssetGrowthRate = toNonNegativeNumber(
      data.otherAssetGrowthRate ?? 0.02
    );
    this.depreciationRate = toNonNegativeNumber(data.depreciationRate ?? 0.15);

    // Portfolio allocation (defaults for backward compatibility)
    this.cashEquivalentsPct = toNonNegativeNumber(data.cashEquivalentsPct ?? 0);
    this.fixedIncomePct = toNonNegativeNumber(data.fixedIncomePct ?? 0);
    this.equitiesPct = toNonNegativeNumber(data.equitiesPct ?? 0);
    this.cashEquivalentsReturn = toNonNegativeNumber(
      data.cashEquivalentsReturn ?? 0
    );
    this.fixedIncomeReturn = toNonNegativeNumber(data.fixedIncomeReturn ?? 0);
    this.equitiesReturn = toNonNegativeNumber(data.equitiesReturn ?? 0);
    this.healthcareInflationBuffer = toNonNegativeNumber(
      data.healthcareInflationBuffer ?? 0
    );
    this.withdrawalOrder = data.withdrawalOrder ?? "standard";
    this.estimatedRetirementTaxRate = toNonNegativeNumber(
      data.estimatedRetirementTaxRate ?? 0
    );
    this.desiredLegacyAmount = toNonNegativeNumber(
      data.desiredLegacyAmount ?? 0
    );
  }

  /**
   * Convert to plain object for JSON serialization
   */
  toJSON(): AssumptionsSettingsData {
    return {
      retirementAge: this.retirementAge,
      lifeExpectancy: this.lifeExpectancy,
      expectedReturnRate: this.expectedReturnRate,
      inflationRate: this.inflationRate,
      expensesAtRetirement: this.expensesAtRetirement,
      realEstateAppreciationRate: this.realEstateAppreciationRate,
      otherAssetGrowthRate: this.otherAssetGrowthRate,
      depreciationRate: this.depreciationRate,
      cashEquivalentsPct: this.cashEquivalentsPct,
      fixedIncomePct: this.fixedIncomePct,
      equitiesPct: this.equitiesPct,
      cashEquivalentsReturn: this.cashEquivalentsReturn,
      fixedIncomeReturn: this.fixedIncomeReturn,
      equitiesReturn: this.equitiesReturn,
      healthcareInflationBuffer: this.healthcareInflationBuffer,
      withdrawalOrder: this.withdrawalOrder,
      estimatedRetirementTaxRate: this.estimatedRetirementTaxRate,
      desiredLegacyAmount: this.desiredLegacyAmount,
    };
  }

  /**
   * Create AssumptionsSettings from plain object
   */
  static fromJSON(data: AssumptionsSettingsData): AssumptionsSettings {
    return new AssumptionsSettings(data);
  }
}

/**
 * Union type of all settings data interfaces (for Firestore and forms)
 */
export type SettingsData =
  | ProfileSettingsData
  | InvestableAssetsSettingsData
  | RealEstateAssetsSettingsData
  | LiabilitiesSettingsData
  | IncomeSettingsData
  | ExpensesSettingsData
  | AssumptionsSettingsData;

/**
 * Settings document in Firestore (includes metadata)
 */
export interface SettingsDocument<T = SettingsData> {
  data: T;
  updatedAt: Date;
  createdAt: Date;
}

/**
 * Type guard to check settings category
 */
export function isSettingsCategory(value: string): value is SettingsCategory {
  return [
    "profile",
    "investableAssets",
    "realEstateAssets",
    "liabilities",
    "income",
    "expenses",
    "assumptions",
  ].includes(value);
}

/**
 * Get the type-safe settings data for a specific category
 */
export type SettingsDataForCategory<T extends SettingsCategory> =
  T extends "profile"
    ? ProfileSettingsData
    : T extends "investableAssets"
      ? InvestableAssetsSettingsData
      : T extends "realEstateAssets"
        ? RealEstateAssetsSettingsData
        : T extends "liabilities"
          ? LiabilitiesSettingsData
          : T extends "income"
            ? IncomeSettingsData
            : T extends "expenses"
              ? ExpensesSettingsData
              : T extends "assumptions"
                ? AssumptionsSettingsData
                : never;
